import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getEmailTemplateSettings, updateEmailTemplateSettings } from '../services/emailService';
import '../styles/EmailTemplateSettings.css';

const EmailTemplateSettings = () => {
  const { currentUser } = useAuth();
  const [settings, setSettings] = useState({
    welcomeTitle: '',
    welcomeSubtitle: '',
    welcomeContent: '',
    welcomeButtonText: '',
    welcomeClosing: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [previewMode, setPreviewMode] = useState(false);

  // Check if current user is admin
  const isAdmin = currentUser?.role === 'Admin';

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    fetchSettings();
  }, [isAdmin]);

  const fetchSettings = async () => {
    try {
      const result = await getEmailTemplateSettings();
      if (result.success) {
        setSettings(result.settings);
      }
    } catch (error) {
      console.error('Error fetching email template settings:', error);
      setMessage('Error loading email template settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    
    try {
      const result = await updateEmailTemplateSettings(settings);
      if (result.success) {
        setMessage('Email template settings saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving email template settings:', error);
      setMessage('Error saving email template settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      welcomeTitle: 'Welcome to Connectional!',
      welcomeSubtitle: 'A Call Management Platform for the TWK',
      welcomeContent: 'Welcome to Connectional! Your account has been successfully created and you\'re now part of the Tennessee-Western Kentucky Conference call management system.',
      welcomeButtonText: 'Access Your Dashboard',
      welcomeClosing: 'If you have any questions or need assistance getting started, please don\'t hesitate to reach out to our support team.'
    });
  };

  if (!isAdmin) {
    return (
      <div className="email-template-settings">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access email template settings.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="email-template-settings">
        <div className="loading">Loading email template settings...</div>
      </div>
    );
  }

  return (
    <div className="email-template-settings">
      <div className="email-template-header">
        <h1>Email Template Settings</h1>
        <p>Customize the welcome email content sent to new users</p>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      <div className="email-template-content">
        <div className="settings-panel">
          <div className="form-group">
            <label htmlFor="welcomeTitle">Welcome Email Title</label>
            <input
              type="text"
              id="welcomeTitle"
              value={settings.welcomeTitle}
              onChange={(e) => handleInputChange('welcomeTitle', e.target.value)}
              placeholder="Welcome to Connectional!"
            />
          </div>

          <div className="form-group">
            <label htmlFor="welcomeSubtitle">Welcome Email Subtitle</label>
            <input
              type="text"
              id="welcomeSubtitle"
              value={settings.welcomeSubtitle}
              onChange={(e) => handleInputChange('welcomeSubtitle', e.target.value)}
              placeholder="A Call Management Platform for the TWK"
            />
          </div>

          <div className="form-group">
            <label htmlFor="welcomeContent">Welcome Message Content</label>
            <textarea
              id="welcomeContent"
              rows="4"
              value={settings.welcomeContent}
              onChange={(e) => handleInputChange('welcomeContent', e.target.value)}
              placeholder="Welcome message content..."
            />
            <small>This is the main welcome message that appears in the email.</small>
          </div>

          <div className="form-group">
            <label htmlFor="welcomeButtonText" style="text-color:#ffffff">Button Text</label>
            <input
              type="text"
              id="welcomeButtonText"
              value={settings.welcomeButtonText}
              onChange={(e) => handleInputChange('welcomeButtonText', e.target.value)}
              placeholder="Access Your Dashboard"
            />
          </div>

          <div className="form-group">
            <label htmlFor="welcomeClosing">Closing Message</label>
            <textarea
              id="welcomeClosing"
              rows="3"
              value={settings.welcomeClosing}
              onChange={(e) => handleInputChange('welcomeClosing', e.target.value)}
              placeholder="Closing message..."
            />
            <small>This message appears after the account details and button.</small>
          </div>

          <div className="form-actions">
            <button 
              className="btn btn-secondary"
              onClick={resetToDefaults}
              type="button"
            >
              Reset to Defaults
            </button>
            <button 
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        <div className="preview-panel">
          <div className="preview-header">
            <h3>Email Preview</h3>
            <button 
              className="btn btn-sm btn-secondary"
              onClick={() => setPreviewMode(!previewMode)}
            >
              {previewMode ? 'Hide Preview' : 'Show Preview'}
            </button>
          </div>

          {previewMode && (
            <div className="email-preview">
              <div className="email-preview-container">
                <div className="email-header">
                  <img src="/images/connectional-logo.svg" alt="Connectional" className="email-logo" />
                  <h1 className="email-title">{settings.welcomeTitle || 'Welcome to Connectional!'}</h1>
                  <p className="email-subtitle">{settings.welcomeSubtitle || 'A Call Management Platform for the TWK'}</p>
                </div>
                
                <div className="email-body">
                  <p>Hello [User Name],</p>
                  
                  <div className="email-content">
                    {settings.welcomeContent || 'Welcome to Connectional! Your account has been successfully created.'}
                  </div>
                  
                  <div className="email-features">
                    <h3>What you can do with Connectional:</h3>
                    <div className="feature-item">✓ Track candidate progress through the ordination process</div>
                    <div className="feature-item">✓ Submit and manage required documents</div>
                    <div className="feature-item">✓ Access CRC (Conference Relations Committee) resources</div>
                    <div className="feature-item">✓ View reports and analytics</div>
                    <div className="feature-item">✓ Collaborate with conference leadership</div>
                  </div>
                  
                  <div className="email-details">
                    <p><strong>Your account details:</strong></p>
                    <ul>
                      <li><strong>Email:</strong> [user@example.com]</li>
                      <li><strong>Role:</strong> [User Role]</li>
                      <li><strong>Conference:</strong> Tennessee-Western Kentucky Conference</li>
                    </ul>
                  </div>
                  
                  <div className="email-button-container">
                    <button className="email-button">
                      {settings.welcomeButtonText || 'Access Your Dashboard'}
                    </button>
                  </div>
                  
                  <p>{settings.welcomeClosing || 'If you have any questions, please reach out to our support team.'}</p>
                  
                  <div className="email-footer">
                    <p>Best regards,<br/>
                    Board of Ordained Ministry<br/>
                    Tennessee-Western Kentucky Conference<br/>
                    The United Methodist Church</p>
                    
                    <p><strong>Support:</strong> support@twkbom.org</p>
                    <p><small>This is an automated message. Please do not reply to this email.</small></p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateSettings;
